import { useCallback, useEffect, useState } from "react";
import {
  AppShell,
  Group,
  Text,
  NavLink,
  Menu,
  ScrollArea,
  UnstyledButton,
  Avatar,
  Divider,
  Badge,
  Image,
  Alert,
  Anchor,
} from "@mantine/core";
import {
  IconLayoutDashboard,
  IconBriefcase2,
  IconLogout,
  IconUser,
  IconUsers,
  IconFileDescription,
  IconShieldCheck,
  IconAlertCircle,
  IconChartLine,
  IconShieldLock,
  IconDatabase,
} from "@tabler/icons-react";
import type { TablerIcon } from "@tabler/icons-react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import trpc from "@/shared/utils/trpc";
import "mantine-react-table/styles.css";

type DecodedToken = {
  id: string;
  email: string;
  role: string;
  exp: number;
};

export function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();

  const [verificationQueueTotal, setVerificationQueueTotal] = useState<number | null>(null);

  const refreshVerificationQueue = useCallback(async () => {
    if (!Cookies.get("token")) {
      setVerificationQueueTotal(null);
      return;
    }
    try {
      const data = await trpc.admin.verificationQueueSummary.query();
      setVerificationQueueTotal(data.total);
    } catch {
      setVerificationQueueTotal(null);
    }
  }, []);

  useEffect(() => {
    void refreshVerificationQueue();
    const intervalId = window.setInterval(() => void refreshVerificationQueue(), 90_000);
    return () => window.clearInterval(intervalId);
  }, [refreshVerificationQueue]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") void refreshVerificationQueue();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [refreshVerificationQueue]);

  // Get admin email from token
  const adminToken = Cookies.get("token");
  let adminEmail = "admin@gtconnect.com";
  if (adminToken) {
    try {
      const decoded = jwtDecode<DecodedToken>(adminToken);
      adminEmail = decoded.email;
    } catch (e) {
      // Ignore decode errors
    }
  }

  const isActive = (path: string) => {
    if (path === "/admin") {
      return location.pathname === "/admin";
    }
    return location.pathname.startsWith(path);
  };

  const navItems: {
    label: string;
    icon: TablerIcon;
    path: string;
    badgeCount?: number | null;
  }[] = [
    { label: "Users", icon: IconUsers, path: "/admin/users" },
    { label: "Onboarding chart", icon: IconChartLine, path: "/admin/company-onboarding" },
    { label: "Companies", icon: IconLayoutDashboard, path: "/admin/companies" },
    {
      label: "Verification queue",
      icon: IconShieldCheck,
      path: "/admin/pending-verification",
      badgeCount: verificationQueueTotal,
    },
    { label: "Indirect registry", icon: IconDatabase, path: "/admin/indirect-verification" },
    { label: "Collaborations", icon: IconBriefcase2, path: "/admin/collaborations" },
    { label: "Interest Management", icon: IconUser, path: "/admin/interest" },
    { label: "Resource", icon: IconFileDescription, path: "/admin/resource" },
    { label: "Authenticator (2FA)", icon: IconShieldLock, path: "/admin/two-factor" },
  ];

  const showVerificationBanner =
    verificationQueueTotal != null &&
    verificationQueueTotal > 0 &&
    !location.pathname.startsWith("/admin/pending-verification");

  const handleNavClick = (path: string) => {
    navigate(path);
  };

  const handleLogout = () => {
    Cookies.remove("token");
    navigate("/admin/login");
  };

  return (
    <AppShell
      navbar={{
        width: 260,
        breakpoint: 0,
      }}
      padding="0"
    >
      <AppShell.Navbar p="md">
        {/* HEADER / BRANDING */}
        <AppShell.Section mb="md">
          <Group gap="xs">
            <Image src="/gtLogo.svg" alt="GTsetu" style={{ height: 38, width: "auto" }} />
            <Badge color="red" size="sm" variant="filled">
              Admin
            </Badge>
          </Group>
          <Divider />
        </AppShell.Section>

        {/* NAV LINKS */}
        <AppShell.Section grow component={ScrollArea}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {navItems.map((item) => (
              <NavLink
                key={item.label}
                label={
                  <Group gap={8} wrap="nowrap" justify="space-between" w="100%">
                    <Text size="sm" fw={400} c={isActive(item.path) ? "primary.7" : undefined} style={{ flex: 1 }}>
                      {item.label}
                    </Text>
                    {item.badgeCount != null && item.badgeCount > 0 ? (
                      <Badge size="sm" variant="filled" color="orange" circle>
                        {item.badgeCount > 99 ? "99+" : item.badgeCount}
                      </Badge>
                    ) : null}
                  </Group>
                }
                leftSection={
                  <item.icon
                    size="1.2rem"
                    stroke={1.8}
                    color={isActive(item.path) ? "var(--mantine-color-primary-7)" : undefined}
                  />
                }
                active={isActive(item.path)}
                onClick={() => handleNavClick(item.path)}
                variant="light"
                color="primary.7"
                styles={{
                  root: {
                    borderRadius: 8,
                    padding: "10px 12px",
                  },
                }}
              />
            ))}
          </div>
        </AppShell.Section>

        {/* ADMIN PROFILE */}
        <AppShell.Section>
          <Menu shadow="md" width={250} position="top-end">
            <Menu.Target>
              <UnstyledButton
                w="100%"
                p="xs"
                style={{
                  borderRadius: 8,
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--mantine-color-gray-1)")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
              >
                <Group>
                  <Avatar color="red" radius="xl">
                    <IconUser size={18} />
                  </Avatar>
                  <div style={{ flex: 1, overflow: "hidden" }}>
                    <Text size="sm" fw={400} truncate>
                      Admin
                    </Text>
                    <Text c="slate.6" size="xs" truncate>
                      {adminEmail}
                    </Text>
                  </div>
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item color="red" leftSection={<IconLogout size={16} />} onClick={handleLogout}>
                Logout
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        {showVerificationBanner ? (
          <Alert
            icon={<IconAlertCircle size={18} />}
            color="orange"
            variant="light"
            title="Companies in verification"
            styles={{ root: { borderRadius: 0 } }}
          >
            <Text size="sm">
              {verificationQueueTotal === 1
                ? "1 company is"
                : `${verificationQueueTotal} companies are`}{" "}
              in under-review or system-verified status and may need your attention.{" "}
              <Anchor
                size="sm"
                fw={500}
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/admin/pending-verification");
                }}
              >
                Open verification queue
              </Anchor>
            </Text>
          </Alert>
        ) : null}
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
